/**
 * The one seam every lookup-enum consumer resolves through.
 *
 * A column behaves as a lookup enum for two different reasons:
 *
 *  - **explicit** — the user declared an `EnumColumnDeclaration` on the column
 *    itself (the `2026-08-28` slice), or
 *  - **implicit** — the column resolves to exactly one unconditional FK target
 *    (`resolveLookupEnumTarget`) whose table carries a
 *    `ReferenceTableDeclaration`: "mark `statuses` once, every column that
 *    references it shows names" (the `2026-08-29` slice).
 *
 * **An explicit declaration ALWAYS wins, whatever its source.** A user who
 * sampled `orders.status_id` into a plain `sampled` enum has said what that
 * column is; a reference-table flag added later must not silently reinterpret
 * it. This is why the explicit arm matches on identity only and never on
 * `source` — callers that need lookup pairs check `declaration.source` for
 * themselves (see `useLookupLabelMaps`).
 *
 * Kept OUT of `lookupEnumTarget.ts` deliberately: that module is a pure
 * FK-shape resolver whose inputs are a data type, a real FK and the loose
 * rels — it knows nothing about MetaInfo. This module is the MetaInfo query
 * (`enumColumns` + `referenceTables`) layered on top of that resolver's
 * output. Different inputs, different reasons to change; both stay small.
 *
 * Pure — no React, no hooks, no I/O.
 *
 * Kusto invariant: schemas may legitimately be `''`. Every comparison here is
 * `?? ''`-normalized — never a truthiness check.
 */

import type {
  DatabaseMetaInfo,
  EnumColumnDeclaration,
  EnumLookupTarget,
  ReferenceTableDeclaration,
} from '@/types/electron';

import { findWithSchemaFold } from './metaInfoSchemaMatch';

/**
 * How a column's key→label pairs are OBTAINED — a property of the TARGET
 * table, never of the declaration:
 *  - `'cached'` (the default, and what an absent `mode` means) — the whole
 *    (capped) pair set is fetched into MetaInfo and read from there;
 *  - `'on-demand'` — pairs are resolved per visible grid window by
 *    `useOnDemandLookupLabels`, ephemerally, and never persisted.
 */
export type ReferenceTableMode = NonNullable<ReferenceTableDeclaration['mode']>;

export type EffectiveLookupDeclaration =
  /** A non-SQL host supplies a concrete lookup and fetch mode. */
  | { kind: 'host'; lookup: EnumLookupTarget; mode: ReferenceTableMode }
  /** A per-column declaration exists — of ANY source. It wins outright. */
  | { kind: 'explicit'; declaration: EnumColumnDeclaration }
  /**
   * No declaration, but the FK target's table is flagged as a reference table.
   * `mode` is the flag's own mode (absent flag mode = `'cached'`).
   */
  | { kind: 'implicit'; lookup: EnumLookupTarget; mode: ReferenceTableMode };

export type EffectiveLookupDeclarationInput = {
  metaInfo: DatabaseMetaInfo | null | undefined;
  /** Source-column identity. `''` is a valid schema (schemaless engines). */
  schema: string;
  table: string;
  column: string;
  /**
   * The column's resolved FK target, i.e. `resolveLookupEnumTarget(...).target`.
   * `null`/`undefined` when the column has no single unconditional target —
   * implicitness is then impossible (an explicit declaration can still win).
   */
  resolvedTarget: EnumLookupTarget | null | undefined;
  /** Storage supplies the relationship explicitly and never relies on a reference-table flag. */
  hostDeclaration?: { lookup: EnumLookupTarget; lookupMode?: ReferenceTableMode };
};

export function resolveEffectiveLookupDeclaration(
  input: EffectiveLookupDeclarationInput,
): EffectiveLookupDeclaration | null {
  const { metaInfo, schema, table, column, resolvedTarget } = input;
  // A host relationship (Azure Table) answers only when the column has no
  // per-column declaration: a declaration of ANY source wins outright, for a
  // host exactly as for SQL (S4 review C, M7 — a declared lookup enum on a
  // storage property is an enum, not an inert on-demand relationship).
  const hostEffective = (): EffectiveLookupDeclaration | null => input.hostDeclaration
    ? { kind: 'host', lookup: input.hostDeclaration.lookup, mode: input.hostDeclaration.lookupMode ?? 'on-demand' }
    : null;
  if (!metaInfo) return hostEffective();

  const sourceSchema = schema ?? '';

  // 1. Explicit, column-level declaration. Path-scoped declarations address a
  //    JSON sub-key, not the column, so they neither win here nor suppress
  //    implicitness below (same identity rule as `enumCacheKey`).
  //    Matched through `findWithSchemaFold` so the legacy sqlite `'' -> 'main'`
  //    fold (pre-2026-05-31 MetaInfo keyed sqlite entries with `schema: ''`)
  //    resolves here exactly as it does at every other renderer-side MetaInfo
  //    lookup. The fold is one-directional, so Kusto's genuine `''` schema
  //    keeps matching only `''` entries.
  const declaration = findWithSchemaFold(
    (metaInfo.enumColumns ?? []).map((d) => ({ ...d, schema: d.schema ?? '' })),
    sourceSchema,
    (d) => !d.path && d.table === table && d.column === column,
  );
  if (declaration) return { kind: 'explicit', declaration };
  const host = hostEffective();
  if (host) return host;

  // 2. Implicit: the resolved target's table is flagged as a reference table.
  if (!resolvedTarget) return null;
  const flagged = findReferenceTable(metaInfo, resolvedTarget);
  return flagged
    ? { kind: 'implicit', lookup: resolvedTarget, mode: flagged.mode ?? 'cached' }
    : null;
}

/**
 * The reference-table flag on the target's table, if any. `''`-normalized.
 *
 * Exported for the surfaces that need the FLAG ITSELF rather than a mode — the
 * FK-popover footer asks "is this target flagged, and in which mode?" to choose
 * between its on-ramp and its off-ramp, and must not re-implement the
 * schema-normalizing match.
 */
export function findReferenceTable(
  metaInfo: DatabaseMetaInfo | null | undefined,
  target: EnumLookupTarget | null | undefined,
): ReferenceTableDeclaration | undefined {
  if (!metaInfo || !target) return undefined;
  const targetSchema = target.toSchema ?? '';
  return (metaInfo.referenceTables ?? []).find(
    (r) => (r.schema ?? '') === targetSchema && r.table === target.toTable,
  );
}

/**
 * How pairs are obtained for anything pointing at this TARGET table. An
 * unflagged target (or none at all) is `'cached'` — the historical behavior,
 * which is also what an absent `mode` on a flag means.
 */
export function referenceTableModeFor(
  metaInfo: DatabaseMetaInfo | null | undefined,
  target: EnumLookupTarget | null | undefined,
): ReferenceTableMode {
  return findReferenceTable(metaInfo, target)?.mode ?? 'cached';
}

/**
 * How THIS column's pairs are obtained.
 *
 * The two questions a lookup column answers are resolved by two different
 * owners: an explicit declaration always wins on *what the column is* (see the
 * module doc), while the target table always wins on *how its pairs are
 * obtained*. So an explicit `source: 'lookup'` declaration pointing at a table
 * the user later flagged `'on-demand'` resolves on demand too — one target,
 * one fetch model, no split-brain between two columns reading the same table.
 */
export function resolveLookupPairsMode(
  metaInfo: DatabaseMetaInfo | null | undefined,
  effective: EffectiveLookupDeclaration,
): ReferenceTableMode {
  if (effective.kind === 'implicit' || effective.kind === 'host') return effective.mode;
  return referenceTableModeFor(metaInfo, effective.declaration.lookup);
}

/**
 * "This column's labels are resolved per visible window, and its pairs are
 * never enumerable" — the one question every enumerate-the-pairs surface asks
 * before offering itself: the blue `▾` glyph and its menu, the enum cell
 * editor, the enum filter picker, "Enum details".
 *
 * Resolves BOTH arms through `resolveLookupPairsMode`, which is what makes an
 * EXPLICIT declaration on an on-demand target behave like an implicit one:
 * declaring `orders.customer_id` says what the column IS, never how a 200k-row
 * `Customers` gives up its pairs. The declaration stays on disk and wins again
 * the moment the flag goes back to `'cached'`.
 *
 * Pure; safe to call per column per render.
 */
export function isOnDemandLookupColumn(input: EffectiveLookupDeclarationInput): boolean {
  const effective = resolveEffectiveLookupDeclaration(input);
  return effective !== null && resolveLookupPairsMode(input.metaInfo, effective) === 'on-demand';
}
